import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ragApi } from '@/lib/api/rag.api';

const filesKey = (configurationId: string) => ['rag', 'files', configurationId];
const sharedKey = (configurationId: string) => ['rag', 'shared', configurationId];

export function useRagFiles(configurationId: string, enabled = true) {
  return useQuery({
    queryKey: filesKey(configurationId),
    queryFn: () => ragApi.listFiles(configurationId),
    enabled: enabled && !!configurationId,
  });
}

export function useUploadRagFile(configurationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => ragApi.uploadFile(configurationId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: filesKey(configurationId) }),
  });
}

export function useIngestRagUrl(configurationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (url: string) => ragApi.ingestUrl(configurationId, url),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: filesKey(configurationId) }),
  });
}

export function useSetRagFileShared(configurationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isShared }: { id: string; isShared: boolean }) =>
      ragApi.setShared(configurationId, id, isShared),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: filesKey(configurationId) }),
  });
}

export function useDeleteRagFile(configurationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ragApi.deleteFile(configurationId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: filesKey(configurationId) }),
  });
}

export function useBackfillRagFileStorage(configurationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      ragApi.backfillStorage(configurationId, id, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: filesKey(configurationId) }),
  });
}

export function useMakeRagFilePublic(configurationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, expiresInDays }: { id: string; expiresInDays?: number }) =>
      ragApi.makePublic(configurationId, id, expiresInDays),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: filesKey(configurationId) }),
  });
}

export function useRevokeRagFilePublic(configurationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ragApi.revokePublic(configurationId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: filesKey(configurationId) }),
  });
}

export function useAvailableSharedFiles(configurationId: string, enabled = true) {
  return useQuery({
    queryKey: sharedKey(configurationId),
    queryFn: () => ragApi.listAvailableSharedFiles(configurationId),
    enabled: enabled && !!configurationId,
  });
}

export function useSubscribeRagFile(configurationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ragFileId: string) => ragApi.subscribe(configurationId, ragFileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: filesKey(configurationId) });
      queryClient.invalidateQueries({ queryKey: sharedKey(configurationId) });
    },
  });
}

export function useUnsubscribeRagFile(configurationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ragFileId: string) => ragApi.unsubscribe(configurationId, ragFileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: filesKey(configurationId) });
      queryClient.invalidateQueries({ queryKey: sharedKey(configurationId) });
    },
  });
}
